import { useEffect, useMemo, useState } from 'react';
import type { IslamicEvent } from '../lib/types';
import { getEffectiveHijriAdjustment } from '../lib/hijriRegional';
import { readJson, writeJson } from '../lib/storage';
import { normalizeISODate, todayISO } from '../lib/time';
import { SettingsDrawer } from '../components/SettingsDrawer';
import { fetchWithCache } from '../lib/appCache';
import { eventsUpcoming } from '../lib/api';
import { CityPicker } from '../components/CityPicker';
import { GeoSwitchBanner } from '../components/GeoSwitchBanner';
import { useLocationState } from '../lib/useLocationState';
import { useSettingsState } from '../lib/useSettingsState';
import { useLocale, HIJRI_MONTHS, eventName, eventCategory } from '../lib/i18n';

export function EventsPage() {
  const { t, locale } = useLocale();
  const { settings, setSettings } = useSettingsState();
  const {
    location,
    setLocation,
    suggestedLocation,
    acceptSuggestedLocation,
    dismissSuggestedLocation
  } = useLocationState();
  const [filterMonth, setFilterMonth] = useState<number | 'all'>('all');
  const today = useMemo(() => todayISO(location?.timezone), [location?.timezone]);
  const [from, setFrom] = useState(() => normalizeISODate(readJson<string>('events_from_date', today), today));
  const [limit, setLimit] = useState<number>(() => readJson<number>('events_limit', 12));
  const [events, setEvents] = useState<IslamicEvent[]>([]);
  const [disclaimer, setDisclaimer] = useState<string>(t.defaultDisclaimer);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => writeJson('events_from_date', from), [from]);
  useEffect(() => writeJson('events_limit', limit), [limit]);

  const effectiveHijriAdjustment = useMemo(() => getEffectiveHijriAdjustment(settings, location), [settings, location]);
  const filtered = useMemo(() => {
    if (filterMonth === 'all') return events;
    return events.filter((event) => event.hijriDate.month === filterMonth);
  }, [events, filterMonth]);

  useEffect(() => {
    const normalized = normalizeISODate(from, today);
    if (normalized !== from) setFrom(normalized);
  }, [from, today]);

  async function refreshEvents() {
    if (!location?.city || !location.country) {
      setEvents([]);
      setError(null);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const key = `events:v1|from=${from}|limit=${limit}|city=${location.city}|country=${location.country}|tz=${location.timezone}|m=${settings.methodId}|md=${settings.madhhab}|ha=${effectiveHijriAdjustment}`;
      const res = await fetchWithCache(
        key,
        () =>
          eventsUpcoming(from, location, settings, limit)
            .then((data) => ({ ok: true as const, data }))
            .catch((e: unknown) => ({ ok: false as const, error: e instanceof Error ? e.message : 'Failed to load events' })),
        { maxAgeMs: 6 * 60 * 60 * 1000 }
      );

      if (res.result.ok) {
        setEvents(res.result.data.events);
        setDisclaimer(res.result.data.disclaimer);
      } else {
        setEvents(res.cached?.value.events ?? []);
        setDisclaimer(res.cached?.value.disclaimer ?? t.defaultDisclaimer);
        setError(res.result.error);
      }
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!location?.city || !location.country) return;
    void refreshEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.city, location?.country, location?.timezone, settings.methodId, settings.madhhab, effectiveHijriAdjustment, from, limit]);

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div className="col" style={{ gap: 6 }}>
          <h1 className="h1">{t.islamicEvents}</h1>
          <div className="p">
            {t.eventsIntro}
          </div>
        </div>

        <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="input" type="date" value={from} onChange={(e) => setFrom(normalizeISODate(e.target.value, from))} style={{ width: 180 }} />
          <button className="btn" onClick={() => setFrom(today)}>{t.today}</button>
          <select className="input" value={String(limit)} onChange={(e) => setLimit(Number(e.target.value))} style={{ width: 110 }}>
            {[6, 10, 12, 18, 24, 30].map((n) => (
              <option key={n} value={String(n)}>{n}</option>
            ))}
          </select>
          <SettingsDrawer value={settings} location={location} onChange={setSettings} />
          <button className="btn" onClick={() => void refreshEvents()} aria-disabled={!location || busy} disabled={!location || busy}>
            {busy ? t.refreshing : t.refresh}
          </button>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        {location && suggestedLocation ? (
          <GeoSwitchBanner
            current={location}
            suggested={suggestedLocation}
            onAccept={acceptSuggestedLocation}
            onDismiss={() => dismissSuggestedLocation(24)}
          />
        ) : null}

        <CityPicker value={location} onChange={setLocation} />
      </div>

      {!location ? (
        <div className="card" style={{ padding: 14, marginTop: 14 }}>
          <div className="h2" style={{ marginBottom: 6 }}>{t.chooseLocation}</div>
          <div className="p">{t.chooseLocationBody}</div>
        </div>
      ) : null}

      {error ? (
        <div className="card" style={{ padding: 14, marginTop: 14, borderColor: 'var(--danger-border)' }}>
          <div className="h2" style={{ marginBottom: 6 }}>{t.couldntRefreshEvents}</div>
          <div className="p">{error}</div>
          <div className="p" style={{ marginTop: 6 }}>{t.showingCached}</div>
        </div>
      ) : null}

      <div className="card" style={{ padding: 14, marginTop: 14 }}>
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div className="h2">{t.filters}</div>
          <select className="input" value={String(filterMonth)} onChange={(e) => setFilterMonth(e.target.value === 'all' ? 'all' : Number(e.target.value))} style={{ width: 260 }}>
            <option value="all">{t.allHijriMonths}</option>
            {HIJRI_MONTHS[locale].map((label, i) => (
              <option key={i + 1} value={String(i + 1)}>{label}</option>
            ))}
          </select>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          <span className="chip">{t.locationLabel}: {location ? `${location.city}, ${location.country}` : '—'}</span>
          <span className="chip">{t.adjustmentPrefix}: {effectiveHijriAdjustment > 0 ? `+${effectiveHijriAdjustment}` : String(effectiveHijriAdjustment)}</span>
        </div>
      </div>

      <div style={{ marginTop: 14 }} className="tableWrap">
        <table style={{ minWidth: 820 }}>
          <thead>
            <tr>
              <th className="sticky">{t.thEvent}</th>
              <th>{t.thCategory}</th>
              <th>{t.thHijri}</th>
              <th>{t.thEstimatedRange}</th>
              <th>{t.thImportance}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={`${e.name}-${e.approxGregorianRange.startISO}`}>
                <td className="sticky" style={{ fontWeight: 800 }}>{eventName(locale, e.hijriDate.month, e.hijriDate.day, e.name)}</td>
                <td>{eventCategory(locale, e.category)}</td>
                <td>
                  {e.hijriDate.day}/{e.hijriDate.month}
                </td>
                <td>
                  {e.approxGregorianRange.startISO} → {e.approxGregorianRange.endISO}
                </td>
                <td>{e.importanceRank}</td>
              </tr>
            ))}
            {!filtered.length ? (
              <tr>
                <td colSpan={5} style={{ padding: 14 }}>{location ? t.noEventsMatch : t.selectLocationLoad}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ padding: 14, marginTop: 16 }}>
        <div className="p">
          <strong>{t.disclaimerLabel}:</strong> {disclaimer}
        </div>
      </div>
    </div>
  );
}
